import { Grid, SvgIcon, Typography } from '@material-ui/core';
import { TextInput } from '@ncr-retail/base-retail-components';
import React, { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  popEmeraldModal,
  pushEmeraldModal,
} from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import BaseButton from '../common/components/base-button';
import EmeraldDialog from '../common/components/emerald-dialog/emerald-dialog';
import { POSModeState } from '../common/interfaces/states/POSModeState';
import { AppState } from '../common/redux/configure-store';
import { ReactComponent as GoBackIcon } from '../transaction-search/media/go-back-icon.svg';
import { tenderVoucherScreenStyles } from './tender-voucher-screen-styles';

type Props = ITenderExchangeVoucherProps;

interface BarcodeInput {
  value: string;
}

const defaultVoucherBarcode: BarcodeInput = {
  value: '',
};

export const ALPHA_NUMERIC_VOUCHER_MODAL = 'ALPHA_NUMERIC_VOUCHER_MODAL';
export const showSearchTenderVoucherModal = (props: Props): void => {
  pushEmeraldModal(
    <TenderExchangePaymentVoucher {...props} />,
    props.onVoucherPayment,
    ALPHA_NUMERIC_VOUCHER_MODAL,
  );
};

const TenderExchangePaymentVoucher: React.FC<Props> = (props: Props): JSX.Element => {
  const classes = tenderVoucherScreenStyles();
  const { t } = useTranslation();
  const leftHanded = useSelector((state: AppState): boolean => state.cashierPreferences.leftHanded);
  const posModeState = useSelector((state: AppState): POSModeState => state.posMode);
  const [voucherBarcode, setVoucherBarcode] = useState<BarcodeInput>(defaultVoucherBarcode);

  const testIdPrefix = 'voucher-barcode-input';
  const titleText = 'Voucher';

  const onTextChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const { value } = event.target;
    setVoucherBarcode({
      value: value,
    });
  };

  const onBack = () => {
    popEmeraldModal(ALPHA_NUMERIC_VOUCHER_MODAL);
    props.onBack();
  };

  const onPayment = (input: string) => {
    props
      .onVoucherPayment(input)
      .then(() => popEmeraldModal(ALPHA_NUMERIC_VOUCHER_MODAL))
      .catch(err => {
        setVoucherBarcode({ value: '' });
        popupMessage(err);
      });
  };

  return (
    <EmeraldDialog
      open={true}
      classes={{
        paper: posModeState.trainingMode ? classes.trainingModeBackground : classes.dialogPaper,
      }}
      fullWidth={true}
      fullScreen={true}
    >
      <Grid
        container
        direction='column'
        className={`fullHeight ${classes.returnItemContainer} ${classes.outerFlexDesgin}`}
      >
        <Grid container direction='column' justifyContent='center' className={`fullWidth`}>
          <Grid item className={classes.fitHeight}>
            <Grid container direction='column' className={`fullWidth`}>
              <Typography
                variant='h2'
                className={`${classes.returnItemTitleHeader}`}
                data-testid={
                  posModeState.trainingMode
                    ? 'tender-voucher-title-container-training'
                    : 'tender-voucher-title-container'
                }
              >
                {t(`${titleText}`)}
              </Typography>
              <Grid item>
                <Typography className={`${classes.returnItemTitleBar}`} />
              </Grid>
            </Grid>
          </Grid>
          <Typography variant='body1'>{t('VoucherSubtitle')}</Typography>
        </Grid>
        <Grid
          item
          container
          className={classes.flexDesign}
          direction={leftHanded ? 'row-reverse' : 'row'}
        >
          <Grid item container className={classes.labelHeadTop} justifyContent='center'>
            <Grid item container className={`${classes.containerAlignment} ${classes.halfWidth}`}>
              <Grid item className={classes.middleContainerStyle}>
                <Grid container direction='column'>
                  <TextInput
                    type='text'
                    value={voucherBarcode.value}
                    onChange={onTextChange}
                    autoComplete='off'
                    id='voucher-barcode'
                    label={'Voucher Barcode'}
                    name='voucher-barcode'
                    className={classes.inputText}
                    autoFocus={true}
                    inputProps={{
                      'data-testid': 'vourcher-barcode',
                      className: 'global-keyboard',
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item className={classes.cancelButton}>
              <BaseButton
                buttonType='white'
                className={classes.actionButtons}
                onClick={onBack}
                data-testid={`${testIdPrefix}-back-button`}
                startIcon={
                  <SvgIcon>
                    <GoBackIcon />
                  </SvgIcon>
                }
              >
                {t('Back')}
              </BaseButton>
              <BaseButton
                buttonType='white'
                className={classes.actionButtons}
                onClick={() => onPayment(voucherBarcode.value)} // Change this when completing
                data-testid={`${testIdPrefix}-confirm-button`}
                disabled={
                  voucherBarcode === defaultVoucherBarcode && voucherBarcode.value.length < 6
                }
              >
                {t('Confirm')}
              </BaseButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </EmeraldDialog>
  );
};

export default TenderExchangePaymentVoucher;

export interface ITenderExchangeVoucherProps {
  onBack: () => void;
  onVoucherPayment: (barcode: string) => Promise<void>;
}
